import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PhoneIcon from '@mui/icons-material/Phone';
import StarIcon from '@mui/icons-material/Star';
import SwapCallsIcon from '@mui/icons-material/SwapCalls';

import { ListItem, ListItemText } from '@mui/material';
import React from 'react';

import {
  CallSegmentAgentDisplayMap,
  CallSegmentListItemDTO,
  completionStatusDisplay,
  TimezoneAbbreviationEnum
} from '_lib/api/apiTypes.shim';
import { Avatar } from '_lib/components/Avatar/Avatar';
import { PrimaryCardHeader } from '_lib/components/CardHeader/CardHeader';
import { FlexCard } from '_lib/components/FlexCard/FlexCard';
import { IconButton } from '_lib/components/IconButton/IconButton';
import { List } from '_lib/components/List/List';
import { ListItemAvatar } from '_lib/components/ListItemAvatar/ListItemAvatar';

import { Tooltip } from '_lib/components/Tooltip/Tooltip';
import { DateFormatEnum, DateUtils } from '_lib/utils/date.utils';

import { AiCallIcon } from '../AiCall/AiCallIcon';

import { IconTypography } from '../IconTypography/IconTypography';
import { appRoutePaths, getFullAppRoutePath } from '../Nav/nav.types';

export type CallSegmentListDisplayProps = {
  currentCallSegmentId: string;
  callSegments: CallSegmentListItemDTO[];
  companyTimezone: TimezoneAbbreviationEnum;
  ref?: React.Ref<HTMLDivElement> | undefined;
};

export const CallSegmentListDisplay = ({
  currentCallSegmentId,
  callSegments,
  companyTimezone,
  ref
}: CallSegmentListDisplayProps) => {
  return (
    <FlexCard ref={ref}>
      <PrimaryCardHeader title={<IconTypography variant='h3' startIcon={<SwapCallsIcon />} text='Call Segments' />} />
      <List dense>
        {callSegments.map((segment, index) => {
          const phoneCall = 'phoneCall' in segment ? segment.phoneCall : undefined;
          const aiCall = 'aiCall' in segment ? segment.aiCall : undefined;

          if (!phoneCall && !aiCall) {
            return null;
          }

          const isCurrentSegment = currentCallSegmentId === phoneCall?.id || currentCallSegmentId === aiCall?.id;

          const agentDisplay = CallSegmentAgentDisplayMap[segment.agent];
          const phoneCallDisplay = phoneCall ? segment.outcome : '';
          const aiCallDisplay = aiCall
            ? `${(aiCall.completionStatus && completionStatusDisplay(aiCall.completionStatus)) || ''} ${segment.outcome ? `(${segment.outcome})` : ''}`
            : '';

          return (
            <ListItem
              divider={index !== callSegments.length - 1}
              key={phoneCall?.id || aiCall?.id || 'should-not-happen'}
              secondaryAction={
                <Tooltip title={isCurrentSegment ? 'Current Segment' : 'View in new tab'} placement='left'>
                  {isCurrentSegment ? (
                    <span>
                      <IconButton disabled>
                        <StarIcon />
                      </IconButton>
                    </span>
                  ) : (
                    <IconButton
                      onClick={() => {
                        if (aiCall) window.open(`${getFullAppRoutePath(appRoutePaths.AiCall)}/${aiCall.id}`, '_blank');
                        else if (phoneCall)
                          window.open(`${getFullAppRoutePath(appRoutePaths.CallLog)}/${phoneCall.id}`, '_blank');
                      }}>
                      {<OpenInNewIcon />}
                    </IconButton>
                  )}
                </Tooltip>
              }>
              <ListItemAvatar>
                <Avatar>
                  {phoneCall?.id && <PhoneIcon />}
                  {aiCall?.id && <AiCallIcon />}
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={`${agentDisplay} \u2014 ${phoneCallDisplay || aiCallDisplay}`.trim()}
                // TODO: 'MMM D, YYYY h:mm:ss a'
                secondary={`${DateUtils.format(segment.startedAt, DateFormatEnum.DateTimeWeekdayShort, {
                  timezone: companyTimezone,
                  showTimezone: true
                })} \u2014 ${segment.durationDisplay}`}
              />
            </ListItem>
          );
        })}
      </List>
    </FlexCard>
  );
};
